<template>
  <div>
    <div v-if="loading" class="spinner mx-auto">
      <a-spin>
        <a-icon slot="indicator" type="loading" style="font-size: 64px" spin />
      </a-spin>
    </div>
    <div class="w-full grid grid-cols-12 gap-4 mb-0">
      <div class="col-span-12 md:col-span-6 text-center">
        <ComplaintsTableHeader :user="user" />
      </div>
      <div class="col-span-12 md:col-span-6">
        <!-- Total solicitudes registradas -->
        <TotalComplaints
          :styleWidth="'w-80'"
          :total="count"
          title="Total de quejas"
          body="quejas"
        />
      </div>
    </div>

    <!-- Filtro de busqueda de solicitudes -->
    <ManagementFilter
      :activeKey="activeTab"
      :loading="loading"
      :searching="searching"
      :complaintType="complaintType"
      @getFilterForm="setFilterForm"
    />

    <!-- Tabla de datos de solicitudes -->
    <div class="w-full grid grid-cols-12 gap-4 mb-2 mx-4 mt-4">
      <div class="col-span-12 text-left">
        <!-- Contenedor principal de las pestañas de Ant Design Vue -->
        <a-tabs :activeKey="activeTab" @change="handleTabChange">
          <!-- Pestaña 1: Ver lista de quejas -->
          <a-tab-pane key="1" tab="Ver lista de quejas">
            <div
              class="bg-white overflow-x-auto rounded-xl border border-gray-300 mx-4 mt-4 search-complaints"
            >
              <!-- Tabla de datos de sesiones -->
              <ManagementTable
                :dataSource="complaints"
                @getReportNonCompliance="setReportNonCompliance"
                @goToComplaintDetail="handleGoToComplaintDetail"
              />

              <div class="p-4 flex justify-end">
                <a-pagination
                  v-model="page"
                  :page-size="pageSize"
                  :page-size-options="pageSizeOptions"
                  :total="count"
                  show-size-changer
                  size="large"
                  @showSizeChange="onShowSizeChange"
                  @change="onChange"
                />
              </div>
            </div>
          </a-tab-pane>

          <!-- Pestaña 2: Ver sesiones en lista  -->
          <a-tab-pane key="2" tab="Ver sesiones en lista">
            <div
              class="bg-white overflow-x-auto rounded-xl border border-gray-300 mx-4 mt-4 search-complaints"
            >
              <!-- Tabla de datos de sesiones -->
              <ManagementAgilizaTable
                :dataSource="sessionsAgiliza"
                @getReportNonCompliance="setReportNonCompliance"
                @accessSession="handleAccessSession"
                @goToComplaintDetail="handleGoToComplaintDetail"
                @viewSessionDetails="handleViewSessionDetails"
              />
            </div>
          </a-tab-pane>
          <!-- Pestaña 3: Ver sesiones en calendario -->
          <a-tab-pane key="3" tab="Ver sesiones en calendario">
            <div class="h-screen max-h-[80vh]">
              <SessionsAgilizaCalendar
                :events="sessionsAgiliza"
                @refresh="fetchData"
              />
            </div>
          </a-tab-pane>
        </a-tabs>
      </div>
    </div>

    <!-- @ok="() => (showReportNonCompliance = false)" -->
    <a-modal
      v-model="showReportNonCompliance"
      centered
      :footer="null"
      :closable="false"
      @ok="() => (showReportNonCompliance = false)"
    >
      <h1 slot="title" class="mt4 mb-0 text-xl text-center text-blue">
        <b>Reportar incumplimiento del acuerdo</b>
      </h1>
      <a-timeline>
        <a-timeline-item>
          El reporte de incumplimiento del acuerdo permite al consumidor
          financiero informar el incumplimiento relacionado con la queja
          presentada en contra de
          <b>{{ formReportNonCompliance.entidad_cod }}</b
          >, por el motivo de <b>{{ formReportNonCompliance.motivo }}</b
          >. Dicha queja fue radicada con el número de radicado
          <b>{{ formReportNonCompliance.codigo_queja }}</b
          >.
        </a-timeline-item>

        <a-timeline-item>
          Una vez realizado el reporte, la Superintendencia Financiera de
          Colombia habilitará un espacio de conciliación virtual en el que usted
          y la entidad contarán con el acompañamiento de un facilitador,
          mediante un chat en línea, con el propósito de promover un acuerdo
          entre las partes.
        </a-timeline-item>

        <a-timeline-item class="pb-0">
          <hr class="my-0" />
          <form
            ref="reportNonComplianceForm"
            @submit.prevent="reportNonCompliance"
            class="w-full px-0 py-0"
          >
            <div class="w-full flex justify-center">
              <div class="grid grid-cols-1 gap-4 w-full">
                <div class="flex justify-center w-full">
                  <BaseTextarea
                    ref="descriptionReportNonCompliance"
                    label="Descripción del incumplimiento del acuerdo"
                    placeholder="Descripción"
                    v-model="descriptionReportNonCompliance"
                    maxlength="4500"
                    required="true"
                  />
                </div>
              </div>
            </div>
            <br />

            <p
              v-if="feedback"
              class="text-xs text-center text-red-500"
              aria-live="polite"
            >
              {{ feedback }}
            </p>

            <div class="flex justify-center mt-3 gap-4">
              <a-button
                class="w-1/3"
                type="primary"
                shape="round"
                :block="true"
                ghost
                @click="hideReportNonCompliance"
              >
                VOLVER
              </a-button>

              <a-button
                type="primary"
                html-type="submit"
                shape="round"
                class="w-1/3"
              >
                <span class="mx-2">ACEPTAR</span>
              </a-button>
            </div>
          </form>
        </a-timeline-item>
      </a-timeline>
    </a-modal>

    <!-- Modal de detalle de sesión -->
    <SessionAgilizaDetailModal
      :visible="isModalVisible"
      :session-data="selectedSessionData"
      @close="closeModal"
      @access="handleAccessSession(selectedSessionData)"
      @goToComplaintDetail="handleGoToComplaintDetail"
      @reschedule="handleRescheduleSession"
    />
  </div>
</template>

<script>
import { mapState } from "vuex";
import ComplaintsTableHeader from "@/components/Customer/Complaint/ComplaintsTableHeader";
import SessionsAgilizaCalendar from "@/feature/customer/complaint/presentation/partials/SessionsAgilizaCalendar.vue";
import ManagementFilter from "@/feature/customer/complaint/presentation/partials/ManagementFilter.vue";
import ManagementTable from "@/feature/customer/complaint/presentation/partials/ManagementTable.vue";
import ManagementAgilizaTable from "@/feature/customer/complaint/presentation/partials/ManagementAgilizaTable.vue";
import TotalComplaints from "@/components/Dashboard/Complaints/TotalComplaints.vue";
import managementServiceImplementation from "@/feature/customer/complaint/data/services/management_services_impl";
import {
  DEFAULT_PAGE,
  DEFAULT_PAGE_SIZE,
  STATE_CF_PENDING_ACTION,
  REPORTAR_INCUMPLIMIENTO_CF,
} from "@/core/utils/constants";
import ErrorMessaje from "@/core/error/error_messaje";
import complaintsAgilizaServicesImplementation from "@/feature/dashboard/complaints_agiliza/data/services/complaints_agiliza_services_impl";

import SessionAgilizaDetailModal from "@/feature/customer/complaint/presentation/partials/SessionAgilizaDetailModal.vue";

export default {
  props: ["complaintType"],
  components: {
    ComplaintsTableHeader,
    TotalComplaints,
    ManagementFilter,
    ManagementTable,
    ManagementAgilizaTable,
    SessionsAgilizaCalendar,
    SessionAgilizaDetailModal,
  },
  data() {
    return {
      activeTab: "1",
      sessionsAgilizaType: "Ver lista de quejas",
      count: 0,
      loading: false,
      form: {},
      formReportNonCompliance: {},
      page: DEFAULT_PAGE,
      pageSize: 25,
      pageSizeOptions: ["25", "50", "100", "150", "200"],
      searching: false,
      complaints: [],
      sessionsAgiliza: [],
      showReportNonCompliance: false,
      descriptionReportNonCompliance: "",
      feedback: "",
      isModalVisible: false,
      selectedSessionData: {},
    };
  },
  methods: {
    handleTabChange(key) {
      switch (key) {
        case "1":
          this.sessionsAgilizaType = "Ver lista de quejas";
          break;
        case "2":
          this.sessionsAgilizaType = "Ver sesiones en lista";
          break;
        case "3":
          this.sessionsAgilizaType = "Ver sesiones en calendario";
          break;
        default:
          this.sessionsAgilizaType = "Ver lista de quejas";
      }
      this.activeTab = key;
    },
    handleGoToComplaintDetail(record) {
      this.$router.push(
        `/customer/complaint/tracking/detail/${record.codigo_queja}?type=agiliza&action=sesion`
      );
    },
    hideReportNonCompliance() {
      this.showReportNonCompliance = false;
    },
    async reportNonCompliance() {
      if (this.validateRequiredField() && this.checkForm()) {
        const codigoQueja = this.formReportNonCompliance.codigo_queja;
        let form = {
          action_type: REPORTAR_INCUMPLIMIENTO_CF,
          descripcion_incumplimiento_negociacion_previa_cf:
            this.descriptionReportNonCompliance,
          reporta_incumplimiento_cf: true,
        };

        // Cargar reporte de incumplimiento por parte de CF
        this.loading = true;
        try {
          const response =
            await complaintsAgilizaServicesImplementation.updateComplaintAgilizaData(
              codigoQueja,
              form,
            );
          if (response.isSuccess) {
            this.showToast(
              "success",
              "Reporte de incumplimiento enviado correctamente",
            );

            this.showReportNonCompliance = false;
            await this.fetchData();
          } else {
            this.showToast("error", ErrorMessaje.generalError);
          }
        } catch (error) {
          this.showToast("error", ErrorMessaje.internalServerError);
        } finally {
          this.loading = false;
        }
      }
    },
    checkForm() {
      let valid = false;

      if (this.descriptionReportNonCompliance.length > 4500) {
        this.feedback =
          "La longitud máxima de la descripción del incumplimiento del acuerdo es 4500.";
        return valid;
      }

      this.feedback = "";
      valid = true;
      return valid;
    },
    validateRequiredField() {
      this.feedback = "";
      let refs = this.$refs;
      let valid = false;

      if (Object.keys(this.form).length === 0) {
        this.feedback = "Complete la información requerida para continuar.";
        return valid;
      }

      for (let ref in refs) {
        if (refs[ref] && refs[ref].required && refs[ref].value === "") {
          let y =
            refs[ref].$el.getBoundingClientRect().top + window.scrollY - 100;
          window.scrollTo(0, y);
          refs[ref].setInvalid(true);
          return valid;
        }
      }
      return true;
    },
    setReportNonCompliance(record) {
      this.formReportNonCompliance.codigo_queja = record.codigo_queja;
      this.formReportNonCompliance.entidad_cod = record.entidad_cod;
      this.formReportNonCompliance.motivo = record.motivo;

      this.showReportNonCompliance = true;
    },
    async setFilterForm(form, pagination) {
      this.form = form;
      if (pagination.page && pagination.pageSize) {
        this.page = pagination.page;
        this.pageSize = pagination.pageSize;
      }
      await this.fetchData();
    },

    async fetchData() {
      this.loading = true;
      this.searching = true;
      const pagination = {
        page: this.page,
        pageSize: this.pageSize,
      };
      let term = {
        codigo_queja: this.form.codigo_queja,
        estado_agiliza: this.form.estado_agiliza,
        estado: this.form.estado,
      };

      switch (this.complaintType) {
        case "todas":
          term.entidad = this.form.entidad;
          term.is_queja_agiliza = "";
          term.motivo = this.form.motivo;
          break;
        case "estandar":
          term.entidad = this.form.entidad;
          term.is_queja_agiliza = "false";
          term.motivo = this.form.motivo;
          break;
        case "agiliza":
          term.agiliza_estado_sesion = this.form.agiliza_estado_sesion;
          term.entidad = this.form.entidad_agiliza;
          term.is_queja_agiliza = "true";
          term.motivo = this.form.motivo_agiliza;
          term.session_start = this.form.session_start;
          term.session_end = this.form.session_end;
          break;
        default:
          break;
      }

      try {
        const { data } =
          await managementServiceImplementation.getCustomerComplaints(
            pagination,
            term,
          );
        this.setDeafultEmited(data);
      } catch (error) {
        this.setDeafultEmited(null);
      } finally {
        this.loading = false;
        this.searching = false;
        // this.sessionsAgiliza = [...this.complaints];

        this.sessionsAgiliza = this.complaints.filter(
          (item) =>
            item.agiliza_data &&
            !Array.isArray(item.agiliza_data) &&
            Array.isArray(item.agiliza_data.sessions) &&
            item.agiliza_data.sessions.length > 0,
        );

        this.sessionsAgiliza = this.sessionsAgiliza
          .filter((item) => item.agiliza_data?.sessions?.length > 0)
          .map((item) => ({
            ...item.agiliza_data.sessions[0],
            reason_name: item.macro_motivo_cod.name,
          }));

        this.sessionsAgiliza = this.sessionsAgiliza.map((element) => {
          const sessionDate = element.session_date
            ? `${this.formatSessionDate(
                element.session_date,
              )} ${this.formatSessionTime(
                element.session_start_time,
              )}  - ${this.formatSessionTime(element.session_end_time)}`
            : "Pendiente";

          const formattedDate = element.creation_date
            ? element.creation_date.replace("T", " ")
            : "";
          return {
            ...element,
            nameEv: element.ev.razon_social,
            session_date_original: element.session_date,
            session_date: sessionDate,
            creation_date: formattedDate,
          };
        });
      }
    },
    setDeafultEmited(resp) {
      this.count = resp?.count || 0;
      const data = resp?.results || [];

      if (data.length > 0) {
        this.complaints = data.map((element) => {
          let estado = element.estado_cod;

          if (element.is_queja_agiliza) {
            if (element.acepta_queja_agiliza !== null) {
              if (
                element.acepta_queja_agiliza &&
                element.agiliza_data &&
                element.agiliza_data.estado
              ) {
                estado = element.agiliza_data.estado.estado_name;
              } else {
                estado = "Declinación voluntaria";
              }
            } else {
              estado = STATE_CF_PENDING_ACTION;
            }
          }

          return {
            ...element,
            estado_cod: estado,
            motivo: element.macro_motivo_cod.name,
            type_complaint_name: element.is_queja_agiliza
              ? "Queja Agiliza"
              : "Queja",
          };
        });
      } else {
        this.complaints = [];
      }
    },
    formatSessionDate(fecha) {
      const [year, month, day] = fecha.split("-");

      const date = new Date(year, month - 1, day);

      const mes = date.toLocaleString("es-ES", {
        month: "long",
      });

      return `${day} de ${
        mes.charAt(0).toUpperCase() + mes.slice(1)
      } de ${year}`;
    },
    formatSessionTime(time) {
      if (!time) {
        return "";
      }

      return new Date(`1970-01-01T${time}`).toLocaleTimeString("es-CO", {
        hour: "numeric",
        minute: "2-digit",
        hour12: true,
      });
    },
    // Cambio de tamaño de página
    onShowSizeChange(page, page_size) {
      // Ante el cambio en el tamaño de la pagina se debe reiniciar la página a 1
      this.page = 1;
      this.pageSize = page_size;
      this.fetchData();
    },
    // Cambio de página
    onChange(page, page_size) {
      this.page = page;
      this.pageSize = page_size;
      this.fetchData();
    },
    showToast(result = "info", msg) {
      this.$notification[result]({
        message: "Resultado",
        description: msg,
        onClick: () => {},
      });
    },
    handleAccessSession(record) {
      if (record && record.session_link) {
        window.open(record.session_link, "_blank");
      }
    },
    async handleRescheduleSession() {
      try {
        const response =
          await complaintsAgilizaServicesImplementation.rescheduleSessionAgiliza(
            this.selectedSessionData.session_agiliza_id,
          );

        if (response.isSuccess) {
          this.showToast("info", response.data.message);
        } else {
          let errorMsg = ErrorMessaje.generalError;
          if (response.data && response.data.message) {
            errorMsg = response.data.message;
          }
          this.showToast("error", errorMsg);
        }
      } catch (error) {
        this.showToast("error", ErrorMessaje.internalServerError);
      } finally {
        this.closeModal();
        this.fetchData();
      }
    },
    handleViewSessionDetails(record) {
      this.selectedSessionData = record;
      this.isModalVisible = true;
    },
    closeModal() {
      this.isModalVisible = false;
      this.selectedSessionData = {};
    },
  },
  computed: mapState({
    user: (state) => {
      return state.session.user;
    },
  }),
};
</script>

<style></style>
